import {FC, useCallback, useMemo} from 'react';
import {Formik} from 'formik';
import {useTranslation} from 'react-i18next';
import {useNavigate, useRevalidator} from 'react-router';
import {AdvisorCodesFirstSection} from '@/components/advisor-profile-form/components/advisor-codes-first-section';
import {AdvisorCodesSecondSection} from '@/components/advisor-profile-form/components/advisor-codes-second-section';
import {AdvisorGeneralAgentSection} from '@/components/advisor-profile-form/components/advisor-general-agent-section';
import {DelegatedAccessSection} from '@/components/advisor-profile-form/components/delegated-access-section';
import {UserContactInfoSection} from '@/components/advisor-profile-form/components/user-contact-info-section';
import {UserGeneralInfoSection} from '@/components/advisor-profile-form/components/user-general-info-section';
import {useAdvisorFormFieldsMetadata} from '@/components/advisor-profile-form/hooks/use-advisor-form-fields-metadata';
import {useAdvisorFormSectionMetadata} from '@/components/advisor-profile-form/hooks/use-advisor-form-section-metadata';
import {useAdvisorProfileFormMessages} from '@/components/advisor-profile-form/hooks/use-advisor-profile-form-messages';
import {useAdvisorProfileFormSchema} from '@/components/advisor-profile-form/hooks/use-advisor-profile-form-schema';
import Button from '@/components/button';
import Message from '@/components/message';
import SectionTitle from '@/components/section-title';
import {APP_PATH} from '@/constants/routes';
import {useUserProfileContext} from '@/contexts/user-profile';
import {SupportedLocale} from '@/initializers/i18next';
import {UserProfileInput} from '@/repositories/beneva-api/endpoints/users';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {WatchDirtyChange} from '../form/watch-dirty-change';

type Props = {
  ['data-testid']?: string;
  onDirtyChange: (isDirty: boolean) => void;
};

const AdvisorProfileForm: FC<Props> = ({onDirtyChange, ...props}) => {
  const {t, i18n} = useTranslation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const {profile, differentFields, saveProfile, isAssistant} = useUserProfileContext();

  const messages = useAdvisorProfileFormMessages();

  const fieldsMetadata = useAdvisorFormFieldsMetadata({
    advisorRole: profile.advisorRole,
    advisorType: profile.advisorType,
  });

  const sectionMetadata = useAdvisorFormSectionMetadata({fieldsMetadata});

  const AdvisorProfileFormSchema = useAdvisorProfileFormSchema({fieldsMetadata});

  const getFieldMessage = useCallback(
    (fieldId: string) => {
      if (differentFields.includes(fieldId)) {
        return t('components.advisor-profile-form.assistive-text.different-value');
      }
      return;
    },
    [t, differentFields],
  );

  const initialValues = useMemo(() => {
    return {
      language: i18n.language as SupportedLocale,
      cons1_lastName: profile.cons1_lastName ?? '',
      cons2_firstName: profile.cons2_firstName ?? '',
      cons4_email: profile.cons4_email ?? '',
      cons5_email_confirmation: profile.cons4_email,
      cons6_phoneNumber: profile.cons6_phoneNumber ?? '',
      cons7_phoneExtension: profile.cons7_phoneExtension ?? null,
      cons8_generalAgentName: profile.cons8_generalAgentName ?? '',
      cons9_generalAgentCode: profile.cons9_generalAgentCode ?? '',
      cons10_generalAgentEmail: profile.cons10_generalAgentEmail ?? '',
      cons11_generalAgentEmail_confirmation: profile.cons10_generalAgentEmail,
      altAdvisorCode1: profile.cons12_altAdvisorCode1,
      altAdvisorCode2: profile.cons13_altAdvisorCode2,
      altAdvisorCode3: profile.cons14_altAdvisorCode3,
      altAdvisorCode4: profile.cons15_altAdvisorCode4,
      cons16_isFundServe: profile.cons16_isFundServe ? 'true' : 'false',
      cons25_advisorCode: profile.cons25_advisorCode ?? '',
    };
  }, [i18n.language, profile]);

  const submitForm = useCallback(
    async (values: typeof initialValues) => {
      const isFundServe = values.cons16_isFundServe === 'true';

      const advisorCode1 = values.altAdvisorCode1 ?? null;
      const advisorCode2 = values.altAdvisorCode2 ?? null;
      const advisorCode3 = values.altAdvisorCode3 ?? null;
      const advisorCode4 = values.altAdvisorCode4 ?? null;

      const cons12_altAdvisorCode1 = advisorCode1 ?? null;
      const cons13_altAdvisorCode2 = advisorCode2 ?? null;
      const cons14_altAdvisorCode3 = advisorCode3 ?? null;
      const cons15_altAdvisorCode4 = advisorCode4 ?? null;

      const {
        altAdvisorCode1,
        altAdvisorCode2,
        altAdvisorCode3,
        altAdvisorCode4,
        cons5_email_confirmation,
        cons11_generalAgentEmail_confirmation,
        ...relevantValues
      } = values;

      const profile: UserProfileInput = {
        ...relevantValues,
        cons16_isFundServe: isFundServe,
        cons12_altAdvisorCode1,
        cons13_altAdvisorCode2,
        cons14_altAdvisorCode3,
        cons15_altAdvisorCode4,
      };

      onDirtyChange(false);

      try {
        const result = await saveProfile(profile);

        if (!result) {
          return;
        }

        navigate(APP_PATH);

        // Reload the advisor profile in the header
        revalidator.revalidate();
      } catch (_error) {
        onDirtyChange(true);
      }
    },
    [onDirtyChange, saveProfile, navigate, revalidator],
  );

  return (
    <Formik {...props} initialValues={initialValues} validationSchema={AdvisorProfileFormSchema} onSubmit={submitForm}>
      {({handleChange, handleBlur, handleSubmit, setFieldValue, values, errors}) => {
        return (
          <>
            <SectionTitle data-testid={`main-form-title-${profile.advisorRole}`}>
              {t(`components.advisor-profile-form.${profile.advisorRole}-info`)}
            </SectionTitle>
            <div className="mx-auto mb-4 flex size-full flex-col">
              {messages.map(
                ({messageKey, show}) =>
                  show && (
                    <Message
                      key={`message-${messageKey}`}
                      dataTestid={`message-${messageKey}`}
                      text={t(`components.advisor-profile-form.messages.${messageKey}`)}
                    />
                  ),
              )}
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-10">
              <div className="gap-4">
                <UserGeneralInfoSection
                  fieldsMetadata={fieldsMetadata}
                  getFieldMessage={getFieldMessage}
                  formik={{handleChange, handleBlur, setFieldValue, values, errors}}
                />

                {sectionMetadata.codeFirstSection && (
                  <AdvisorCodesFirstSection
                    fieldsMetadata={fieldsMetadata}
                    getFieldMessage={getFieldMessage}
                    formik={{handleChange, handleBlur, values, errors}}
                  />
                )}

                {sectionMetadata.codeSecondSection && (
                  <AdvisorCodesSecondSection
                    fieldsMetadata={fieldsMetadata}
                    getFieldMessage={getFieldMessage}
                    formik={{handleChange, handleBlur, values, errors}}
                  />
                )}

                <UserContactInfoSection
                  fieldsMetadata={fieldsMetadata}
                  formik={{handleChange, handleBlur, values, errors}}
                />

                {sectionMetadata.generalAgentSection && (
                  <AdvisorGeneralAgentSection
                    fieldsMetadata={fieldsMetadata}
                    formik={{handleChange, handleBlur, values, errors}}
                  />
                )}
              </div>

              {isAssistant && (
                <DelegatedAccessSection
                  advisorRole={profile.advisorRole}
                  delegatedAdvisors={profile.delegatedAdvisors}
                  orphanCodes={profile.orphanCodes}
                />
              )}

              <div className="flex justify-end">
                <Button
                  onClick={() => {}}
                  type="submit"
                  label={t('components.advisor-profile-form.submit')}
                  trackingKey={WEB_CLICK_TRACKING_KEY.SAVE_ADVISOR_PROFILE}
                  data-testid="button-save-profile-form"
                />
              </div>
            </form>

            <WatchDirtyChange onDirtyChange={onDirtyChange} />
          </>
        );
      }}
    </Formik>
  );
};

export default AdvisorProfileForm;
